/**
 * Questionnaire de candidature formateur, repris du premier SaaS (parcours « /candidature » en 8 étapes du 11/10/2026)
 * et adapté au nouveau : une question par écran, dans un ordre logique (vous → expertise → expérience → façon de
 * former → structure → pièces → engagements → envoi), des menus déroulants pour les choix uniques, des cartes à cocher
 * pour les choix multiples.
 *
 * Module PUR (ni React, ni base) : partagé par l'interface (validation par étape, récapitulatif) et par le serveur
 * (liste des manques qui bloque l'envoi). Les clés des réponses sont les noms des colonnes de la table `formateur`.
 */
import { siretValide as siretValideBase } from "../entreprises/siret";
import { MODALITES, REGIONS_DREETS, STATUTS_JURIDIQUES } from "../pedagogie/listes";

// ——— Référentiels (options exactes de l'ancien parcours, adaptées au nouveau) ———

/** Ancienneté de formateur — menu déroulant (ancien : 4 cartes, mêmes valeurs). */
export const EXPERIENCES = [
  { valeur: "moins_2", libelle: "Moins de 2 ans", detail: "Vous démarrez" },
  { valeur: "2_5", libelle: "2 à 5 ans", detail: "Vous avez vos repères" },
  { valeur: "5_10", libelle: "5 à 10 ans", detail: "Formateur confirmé" },
  { valeur: "plus_10", libelle: "Plus de 10 ans", detail: "Formateur expert" },
] as const;

/**
 * Publics formés — choix multiple (options de l'ancien parcours). « Alternants et jeunes actifs » est retiré de l'écran
 * le 31/10/2026 (recette du dirigeant) : la valeur `alternants` reste ACCEPTÉE (`VALEURS_PUBLICS`) pour qu'un profil qui
 * l'avait cochée s'enregistre sans perte, mais elle n'est plus proposée ni affichée.
 */
export const PUBLICS = [
  { valeur: "salaries", libelle: "Salariés" },
  { valeur: "managers", libelle: "Managers et dirigeants" },
  { valeur: "demandeurs_emploi", libelle: "Demandeurs d'emploi" },
  { valeur: "independants", libelle: "Indépendants et TPE" },
  { valeur: "particuliers_cpf", libelle: "Particuliers (CPF)" },
] as const;
const PUBLICS_RETIRES = ["alternants"] as const;
export const publicAffiche = (v: string) => PUBLICS.some((p) => p.valeur === v);

/**
 * Domaines d'expertise du formateur — menu déroulant (31/10/2026). Liste fermée = les catégories du catalogue de la
 * vitrine (`vitrine/src/lib/categories.ts`, libellés identiques), plus « Autre » avec précision libre. Plusieurs domaines
 * restent possibles (une ligne de menu par domaine). Une valeur enregistrée hors liste n'est jamais perdue : elle
 * s'affiche comme « Autre » avec sa valeur en précision (`lignesDomaines`).
 */
export const DOMAINES_EXPERTISE = [
  "Bien-être",
  "Bureautique & digital",
  "Communication",
  "Business & création d’entreprise",
  "Langues",
  "Métier spécifique",
  "RH & Management",
  "RSE",
  "Ventes",
  "Autre",
] as const;
export const AUTRE_DOMAINE = "Autre";

export type LigneDomaine = { choix: string; precision: string };

/** Valeurs enregistrées → lignes de menus (une valeur hors liste devient « Autre » + précision, sans perte). */
export function lignesDomaines(domaines: readonly string[], autre = ""): LigneDomaine[] {
  return domaines.map((d) => {
    if (d === AUTRE_DOMAINE) return { choix: AUTRE_DOMAINE, precision: autre };
    if ((DOMAINES_EXPERTISE as readonly string[]).includes(d)) return { choix: d, precision: "" };
    return { choix: AUTRE_DOMAINE, precision: d };
  });
}

/**
 * Lignes de menus → valeurs à enregistrer. « Autre » précisé est enregistré par sa précision (ainsi plusieurs « Autre »
 * coexistent) ; « Autre » non précisé reste « Autre », ce que la validation signale. Lignes vides et doublons écartés.
 */
export function domainesDepuisLignes(lignes: readonly LigneDomaine[]): { formateur_domaines: string[]; formateur_domaine_autre: string } {
  const valeurs: string[] = [];
  for (const l of lignes) {
    if (!l.choix) continue;
    const v = l.choix === AUTRE_DOMAINE ? l.precision.trim() || AUTRE_DOMAINE : l.choix;
    if (!valeurs.includes(v)) valeurs.push(v);
  }
  return { formateur_domaines: valeurs, formateur_domaine_autre: "" };
}

/** Libellé affichable d'un domaine enregistré (« Autre : … » pour une valeur hors liste). */
export function libelleDomaine(d: string, autre = ""): string {
  if (d === AUTRE_DOMAINE) return autre ? `Autre : ${autre}` : "Autre";
  return (DOMAINES_EXPERTISE as readonly string[]).includes(d) ? d : `Autre : ${d}`;
}

/** Précision affichée sous chaque modalité (reprise de l'ancien parcours). */
export const DETAILS_MODALITES: Record<string, string> = {
  presentiel: "En salle, chez le client",
  distanciel: "Classe virtuelle",
  mixte: "Présentiel et distanciel",
};

/** Situation « pas encore de structure » de l'ancien parcours : ajoutée à la liste des statuts juridiques. */
export const SANS_STRUCTURE = "Pas encore de structure";
export const STATUTS_CANDIDATURE = [...STATUTS_JURIDIQUES.filter((s) => s !== "Autre"), SANS_STRUCTURE, "Autre"] as const;
/** Statuts pour lesquels un SIRET est attendu (une structure immatriculée). */
const STATUTS_AVEC_SIRET = new Set<string>(["Entreprise individuelle / micro-entreprise", "EURL", "SASU", "SARL", "SAS"]);
export const siretAttendu = (statut: string) => STATUTS_AVEC_SIRET.has(statut);

export const libelleExperience = (v: string) => EXPERIENCES.find((e) => e.valeur === v)?.libelle ?? v;
export const libellePublic = (v: string) => PUBLICS.find((p) => p.valeur === v)?.libelle ?? v;
export const libelleModalite = (v: string) => MODALITES.find((m) => m.valeur === v)?.libelle ?? v;

export const VALEURS_EXPERIENCE = EXPERIENCES.map((e) => e.valeur) as [string, ...string[]];
export const VALEURS_PUBLICS = [...PUBLICS.map((p) => p.valeur), ...PUBLICS_RETIRES] as unknown as [string, ...string[]];
export const VALEURS_MODALITES = MODALITES.map((m) => m.valeur) as [string, ...string[]];
export { MODALITES, REGIONS_DREETS };

// ——— Étapes ———

export const ETAPES = [
  { id: "vous", titre: "Vous", question: "Faisons connaissance", aide: "Ces coordonnées permettent à l'équipe de vous recontacter.", message: "Cinq minutes pour vous présenter, et nous portons le reste." },
  { id: "expertise", titre: "Expertise", question: "Dans quels domaines formez-vous ?", aide: "Choisissez votre domaine d'expertise ; ajoutez-en d'autres si besoin.", message: "Votre expertise est le point de départ de chaque formation que nous porterons." },
  { id: "experience", titre: "Expérience", question: "Votre expérience de formateur", aide: "Pour orienter vers vous les projets qui vous ressemblent.", message: "Débutant ou chevronné : le portage s'adapte à votre rythme." },
  { id: "pratique", titre: "Façon de former", question: "Comment aimez-vous former ?", aide: "Vos modalités et votre profil LinkedIn.", message: "En salle, à distance ou les deux : vous gardez votre façon de transmettre." },
  { id: "structure", titre: "Structure", question: "Votre structure", aide: "Ces informations figureront sur votre contrat de sous-traitance.", message: "Pas encore de structure ? Nous vous aidons à choisir la bonne." },
  { id: "pieces", titre: "Pièces", question: "Vos pièces justificatives", aide: "CV, parcours de formation, déroulé pédagogique, récépissé de NDA… PDF, Word ou image, transmis de façon confidentielle à l'organisme.", message: "Quelques documents suffisent pour que l'équipe pédagogique étudie votre profil." },
  { id: "engagements", titre: "Engagements", question: "Nos engagements communs", aide: "La qualité Qualiopi repose sur chacun des formateurs du réseau.", message: "Un réseau exigeant, au service d'apprenants qui comptent sur nous." },
  { id: "envoi", titre: "Envoi", question: "Vérifiez et envoyez", aide: "Relisez vos réponses : chaque ligne se modifie d'un clic.", message: "Votre espace formateur vous attend : dossiers, pièces et signatures en ligne." },
] as const;

export type EtapeId = (typeof ETAPES)[number]["id"];
export const NB_ETAPES = ETAPES.length;

/** Pourcentage de la barre de progression : l'étape courante compte comme « en cours ». */
export function progression(index: number): number {
  const i = Math.min(Math.max(index, 0), NB_ETAPES - 1);
  return Math.round(((i + 1) / NB_ETAPES) * 100);
}

// ——— Réponses ———

export type Reponses = {
  formateur_prenom: string;
  formateur_nom: string;
  formateur_telephone: string;
  formateur_domaines: string[];
  formateur_domaine_autre: string;
  formateur_specialite: string;
  formateur_experience: string;
  formateur_publics: string[];
  formateur_modalites: string[];
  formateur_linkedin: string;
  formateur_site_structure: string;
  formateur_statut_juridique: string;
  formateur_entreprise_siret: string;
  formateur_entreprise_nom: string;
  formateur_entreprise_adresse: string;
  formateur_nda_numero: string;
  /** « Je n'ai pas de NDA » : dispense du numéro et du récépissé (le formateur porté forme sous la déclaration de l'organisme). */
  formateur_sans_nda: boolean;
  formateur_dreets_region: string;
  formateur_message: string;
  engagement_qualite: boolean;
};

/** Pièces obligatoires de la candidature (reflet de `TYPES_PIECE_FORMATEUR` côté serveur). */
export type PieceDeposee = { type: string };
/** `sauf_sans_nda` : exigée sauf si le formateur déclare « Je n'ai pas de NDA » (récépissé de déclaration d'activité). */
export type TypePiece = { type: string; libelle: string; obligatoire: boolean; sauf_sans_nda?: boolean };

/** Les pièces exigées pour CE formateur (le récépissé de NDA tombe avec la mention « Je n'ai pas de NDA »). */
export function piecesExigees<T extends TypePiece>(r: Pick<Reponses, "formateur_sans_nda">, types: readonly T[]): T[] {
  return types.filter((t) => t.obligatoire || (t.sauf_sans_nda === true && !r.formateur_sans_nda));
}

/** Libellés des pièces exigées non déposées, dans l'ordre de la liste des types. */
export function piecesManquantes(r: Pick<Reponses, "formateur_sans_nda">, pieces: readonly PieceDeposee[], types: readonly TypePiece[]): string[] {
  return piecesExigees(r, types).filter((t) => !pieces.some((p) => p.type === t.type)).map((t) => t.libelle);
}

// ——— Contrôles ———

export const chiffres = (v: string) => v.replace(/\D/g, "");

export function telephoneValide(v: string): boolean {
  const n = chiffres(v).length;
  return /^[+\d\s().-]+$/.test(v.trim()) && n >= 9 && n <= 15;
}

/** SIRET : 14 chiffres (espaces tolérés) et clé de Luhn — la règle vit dans `entreprises/siret.ts` (exception La Poste comprise). */
export const siretValide = (v: string): boolean => /^[\d\s]+$/.test(v.trim()) && siretValideBase(v);

/** Numéro de déclaration d'activité : 11 chiffres (espaces tolérés). */
export const ndaValide = (v: string) => /^\d{11}$/.test(v.replace(/\s/g, ""));

export function lienProfilValide(v: string): boolean {
  const t = v.trim();
  if (!t) return true;
  try {
    const u = new URL(t);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** Complète « linkedin.com/in/x » en « https://linkedin.com/in/x » (l'ancien parcours l'acceptait sans protocole). */
export function normaliserLien(v: string): string {
  const t = v.trim();
  // Une adresse qui porte déjà un protocole (« ftp://… ») reste telle quelle : le contrôle http(s) la refusera.
  if (!t || /^[a-z][a-z0-9+.-]*:\/\//i.test(t)) return t;
  return `https://${t}`;
}

export type Erreurs = Partial<Record<keyof Reponses | string, string>>;

/** Erreurs de l'étape `id` (objet vide : étape valide). Les champs facultatifs ne bloquent jamais. */
export function validerEtape(id: EtapeId, r: Reponses, pieces: PieceDeposee[] = [], types: readonly TypePiece[] = []): Erreurs {
  const e: Erreurs = {};
  switch (id) {
    case "vous":
      if (!r.formateur_prenom.trim()) e.formateur_prenom = "Indiquez votre prénom.";
      if (!r.formateur_nom.trim()) e.formateur_nom = "Indiquez votre nom.";
      if (!r.formateur_telephone.trim()) e.formateur_telephone = "Indiquez un numéro de téléphone, par exemple 06 12 34 56 78.";
      else if (!telephoneValide(r.formateur_telephone)) e.formateur_telephone = "Ce numéro ne semble pas valide : 9 à 15 chiffres, par exemple 06 12 34 56 78.";
      break;
    case "expertise":
      if (r.formateur_domaines.length === 0) e.formateur_domaines = "Choisissez votre domaine d'expertise.";
      if (r.formateur_domaines.includes("Autre") && !r.formateur_domaine_autre.trim()) e.formateur_domaine_autre = "Précisez votre domaine.";
      if (r.formateur_specialite.length > 200) e.formateur_specialite = "200 caractères maximum.";
      break;
    case "experience":
      if (!r.formateur_experience) e.formateur_experience = "Choisissez votre ancienneté de formateur.";
      if (r.formateur_publics.length === 0) e.formateur_publics = "Choisissez au moins un public.";
      break;
    case "pratique":
      if (r.formateur_modalites.length === 0) e.formateur_modalites = "Choisissez au moins une modalité.";
      if (!lienProfilValide(normaliserLien(r.formateur_linkedin))) e.formateur_linkedin = "Collez l'adresse de votre profil LinkedIn, par exemple linkedin.com/in/votre-nom.";
      break;
    case "structure":
      if (!r.formateur_statut_juridique) e.formateur_statut_juridique = "Choisissez votre situation.";
      if (siretAttendu(r.formateur_statut_juridique)) {
        if (!chiffres(r.formateur_entreprise_siret)) e.formateur_entreprise_siret = "Indiquez le SIRET de votre structure (14 chiffres).";
        else if (!siretValide(r.formateur_entreprise_siret)) e.formateur_entreprise_siret = "Ce SIRET ne semble pas valide : vérifiez les 14 chiffres.";
      }
      if (!r.formateur_sans_nda && !r.formateur_nda_numero.trim()) e.formateur_nda_numero = "Indiquez votre numéro de déclaration d'activité (11 chiffres), ou indiquez que vous n'en avez pas.";
      else if (!r.formateur_sans_nda && !ndaValide(r.formateur_nda_numero)) e.formateur_nda_numero = "Le numéro de déclaration d'activité comporte 11 chiffres.";
      if (!lienProfilValide(normaliserLien(r.formateur_site_structure))) e.formateur_site_structure = "Collez l'adresse du site de votre structure, par exemple www.ma-structure.fr.";
      break;
    case "pieces":
      for (const t of piecesExigees(r, types)) if (!pieces.some((p) => p.type === t.type)) e[`piece_${t.type}`] = `Déposez : ${t.libelle}.`;
      break;
    case "engagements":
      if (!r.engagement_qualite) e.engagement_qualite = "Cochez la case pour confirmer vos engagements.";
      if (r.formateur_message.length > 1500) e.formateur_message = "1 500 caractères maximum.";
      break;
    case "envoi":
      break;
  }
  return e;
}

/** Index de la première étape incomplète (avant l'envoi), ou -1 si tout est prêt. */
export function premiereEtapeIncomplete(r: Reponses, pieces: PieceDeposee[], types: readonly TypePiece[]): number {
  for (let i = 0; i < NB_ETAPES - 1; i++) if (Object.keys(validerEtape(ETAPES[i]!.id, r, pieces, types)).length > 0) return i;
  return -1;
}

/** Libellés courts des manques, dans l'ordre du parcours : c'est la liste qui bloque l'envoi côté serveur. */
const LIBELLES_MANQUES: Record<string, string> = {
  formateur_prenom: "Prénom",
  formateur_nom: "Nom",
  formateur_telephone: "Téléphone",
  formateur_domaines: "Domaine d'expertise",
  formateur_domaine_autre: "Précision du domaine « Autre »",
  formateur_specialite: "Spécialité (200 caractères maximum)",
  formateur_experience: "Ancienneté de formateur",
  formateur_publics: "Publics formés",
  formateur_modalites: "Modalités d'intervention",
  formateur_linkedin: "Profil LinkedIn (adresse web)",
  formateur_site_structure: "Site de la structure (adresse web)",
  formateur_statut_juridique: "Situation (statut juridique)",
  formateur_entreprise_siret: "SIRET de votre structure",
  formateur_nda_numero: "N° de déclaration d'activité (11 chiffres) ou mention « Je n'ai pas de NDA »",
  engagement_qualite: "Engagement qualité (Qualiopi et déontologie)",
  formateur_message: "Message (1 500 caractères maximum)",
};

export function manquesQuestionnaire(r: Reponses, pieces: PieceDeposee[], types: readonly TypePiece[]): string[] {
  const manques: string[] = [];
  for (const etape of ETAPES) {
    for (const cle of Object.keys(validerEtape(etape.id, r, pieces, types))) {
      if (cle.startsWith("piece_")) manques.push(types.find((t) => `piece_${t.type}` === cle)?.libelle ?? cle);
      else manques.push(LIBELLES_MANQUES[cle] ?? cle);
    }
  }
  return manques;
}
